import React from "react"

const defaultIndicatorOptions = {
	activeFontSize: 20,
	inactiveFontSize: 15,
	inactiveOpacity: 0.4,
	activeClassName: "sort-active",
	inactiveClassName: "text-muted",
}

/**
 * Иконка направления сортировки для заголовка таблицы (Unicons: uil-angle-up / uil-angle-down).
 *
 * @param {string} columnKey — значение `order_by` для этой колонки
 * @param {{ order_by?: string | null, sort?: string }} sortState — текущие поля фильтра
 * @param {object} [options]
 * @param {number} [options.activeFontSize]
 * @param {number} [options.inactiveFontSize]
 * @param {number} [options.inactiveOpacity]
 * @param {string} [options.activeClassName]
 * @param {string} [options.inactiveClassName]
 * @param {string} [options.className] — доп. классы на `<i>`
 */
export function renderSortIndicator(columnKey, sortState = {}, options = {}) {
	const {
		activeFontSize,
		inactiveFontSize,
		inactiveOpacity,
		activeClassName,
		inactiveClassName,
		className: extraClassName,
	} = { ...defaultIndicatorOptions, ...options }

	const orderBy = sortState.order_by ?? null
	const sort = sortState.sort === "desc" ? "desc" : "asc"
	const active = orderBy === columnKey
	const isDesc = active && sort === "desc"
	const iconClass = isDesc ? "uil-angle-down" : "uil-angle-up"
	const className = ["uil", iconClass, active ? activeClassName : inactiveClassName, extraClassName]
		.filter(Boolean)
		.join(" ")

	return React.createElement("i", {
		className,
		style: {
			fontSize: active ? activeFontSize : inactiveFontSize,
			lineHeight: 1,
			flexShrink: 0,
			opacity: active ? 1 : inactiveOpacity,
		},
		"aria-hidden": true,
	})
}

/**
 * Обновление фильтра при клике по сортируемой колонке: та же колонка — переключение asc/desc,
 * другая — order_by и sort=asc. `page` сбрасывается в `null`.
 *
 * @param {object} filterData
 * @param {string} columnKey
 * @param {object} [extraPatch] — доп. поля (мерджатся до сброса page)
 */
export function patchSortFilterOnColumnClick(filterData, columnKey, extraPatch = {}) {
	const next = { ...filterData, ...extraPatch, page: null }
	if (filterData.order_by === columnKey) {
		next.sort = filterData.sort === "asc" ? "desc" : "asc"
	} else {
		next.order_by = columnKey
		next.sort = "asc"
	}
	return next
}

/**
 * Одна иконка направления сортировки (для подписи фильтра, тултипа и т.п.).
 *
 * @param {string} [sort] — `'desc'` или любое другое значение считается asc
 * @param {object} [options]
 * @param {number} [options.fontSize]
 * @param {string} [options.className]
 * @param {string} [options.title]
 * @param {string} [options.ariaLabel]
 */
export function renderSortDirectionIcon(sort, options = {}) {
	const isDesc = sort === "desc"
	const iconClass = isDesc ? "uil-angle-down" : "uil-angle-up"
	const { fontSize = 16, className = "sort-active", title, ariaLabel } = options
	const props = {
		className: ["uil", iconClass, className].filter(Boolean).join(" "),
		style: { fontSize, lineHeight: 1, verticalAlign: "middle" },
	}
	if (title) props.title = title
	if (ariaLabel) props["aria-label"] = ariaLabel
	else props["aria-hidden"] = true
	return React.createElement("i", props)
}
